import {
	getPaddockQueryOptions,
	listPaddocksByOrganizationQueryOptions,
} from "@packages/api-client/hooks/paddockController";
import type { GeoJSONPolygon, Paddock } from "@packages/shared-types/grass";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUpdatePaddock } from "@/api/grass";
import { PageHeader } from "@/components/PageHeader";
import { FormSkeleton } from "@/components/ui/skeleton";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { PaddockForm } from "../-components/PaddockForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId/edit",
)({
	// Edits are conflict-prone and never offline (roadmap rule 2).
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: EditPaddockPage,
});

function EditPaddockPage() {
	const { orgId, farmId, paddockId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data, isLoading } = useQuery(
		getPaddockQueryOptions(orgId, farmId, paddockId),
	);

	// Sibling paddocks render as muted context on the boundary map.
	const { data: paddocksResponse } = useQuery(
		listPaddocksByOrganizationQueryOptions(orgId, { establishmentId: farmId }),
	);

	const contextPaddocks = useMemo(
		() =>
			(paddocksResponse?.data ?? []).flatMap((p) =>
				p.id !== paddockId && p.boundaryGeoJson
					? [
							{
								id: p.id,
								name: p.name,
								boundary: p.boundaryGeoJson as GeoJSONPolygon,
							},
						]
					: [],
			),
		[paddocksResponse, paddockId],
	);

	const updateMutation = useUpdatePaddock();

	const handleSubmit = (
		formData: Parameters<typeof updateMutation.mutate>[0]["data"],
	) =>
		updateMutation
			.mutateAsync({
				organizationId: orgId,
				establishmentId: farmId,
				paddockId,
				data: formData,
			})
			.then(() => {
				navigate({
					to: "/app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId",
					params: { orgId, farmId, paddockId },
				});
			});

	if (isLoading) {
		return <FormSkeleton />;
	}

	if (!data) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">Paddock not found</p>
			</div>
		);
	}

	return (
		// Same shell as the create page: the form's panels sit on the page below
		// lg, the card frames them (and contains the scroll) from lg up.
		<div className="-mt-3 flex flex-col gap-2 md:mt-0 md:gap-3 lg:h-full">
			<PageHeader title={t("grass.paddock.edit.title")} showBackButton />
			<div className="lg:card lg:flex-1 lg:min-h-0 lg:overflow-hidden lg:bg-base-100 lg:shadow-md">
				<div className="lg:card-body lg:h-full lg:min-h-0 lg:p-4">
					<PaddockForm
						paddock={data as unknown as Paddock}
						onSubmit={handleSubmit}
						isSubmitting={updateMutation.isPending}
						onCancel={() =>
							navigate({
								to: "/app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId",
								params: { orgId, farmId, paddockId },
							})
						}
						layout="side-by-side"
						contextPaddocks={contextPaddocks}
					/>
				</div>
			</div>
		</div>
	);
}
